import { keyBy, groupBy, keys, findIndex } from 'lodash/fp';
import {
  ObjectStatuses,
  EntityActions,
  EntityCodes,
} from '../global-entities/global-enums';
import { ListRequest } from '../request-models/request-models';
import { CacheManagementService } from '@core/services/cache-management.service';
import { ListResponse } from '../response-models/response-models';

export interface ITaskGroupInfo {
  TaskGroupId?: number;
  Name: string;
  PropertyId: number;
  Status: ObjectStatuses;
}

export class TaskGroupInfo implements ITaskGroupInfo {
  public TaskGroupId?: number;
  public Name: string;
  public PropertyId: number;
  public Status: ObjectStatuses;
}

export interface ITaskInfo {
  TaskId?: number;
  Name: string;
  Notes: string;
  PropertyId: number;
  TaskGroupId: number;
  Status: ObjectStatuses;
  EstimatedTime?: number;
}

export interface IStaffAssignable {
  StaffId?: number;
  StaffName?: string;
}

export type IStaffAssignableTask = IStaffAssignable & ITaskInfo;

export class TaskInfo implements ITaskInfo {
  public TaskId?: number;
  public Name: string;
  public Notes: string;
  public PropertyId: number;
  public TaskGroupId: number;
  public Status: ObjectStatuses;
  public EstimatedTime?: number;
}

export type TaskInfoListResponse = ListResponse<TaskInfo>;

export class TaskGroupInfoViewModel<
  T extends ITaskInfo = TaskInfo
> extends TaskGroupInfo {
  public Tasks?: Array<T>;
  public TotalTime: number;
}

export class TaskSearch extends ListRequest {
  public Tasks: Array<number>;
  public Name: string;
  public PropertyId: number;
  public TaskGroupId?: number;
}

export class TaskGroupSearch extends ListRequest {
  public TaskGroupId?: number;
  public Name: string;
  public Properties: Array<number>;
}

export class TaskUpdate {
  public TaskId?: number;
  public Name: string;
  public Action: EntityActions;
  public Notes: string;
  public PropertyId: number;
  public TaskGroupId: number;
  public EstimatedTime?: number;
  public title?: string;
  public placeHolder?: string;
  public isRequired?: boolean;
  public setDefault() {
    this.Name = '';
    this.EstimatedTime = null;
    this.Notes = '';
  }
}

export class TaskGroupUpdate {
  public TaskGroupId?: number;
  public Name: string;
  public PropertyId: number;
  public Action: EntityActions;
}

export class TaskGroupUIModel<
  T extends ITaskInfo = TaskUIModel
> extends TaskGroupInfoViewModel<T> {
  public PropertyName: string;
  public Selected: boolean;
  public Indeterminate: boolean;
  public AssignedStaffName: string;
  public AssignedStaffId: number;
  public Expanded: boolean;
}

export class TaskUIModel extends TaskInfo implements IStaffAssignable {
  public Selected: boolean;
  public StaffName: string;
  public StaffId: number;
}

export function TransformTaskGroup(
  TaskGroups,
  Tasks,
  cacheManagementService: CacheManagementService
) {
  const TasksById = keyBy<any>('TaskId')(Tasks);
  return TaskGroups.map((group) => {
    group.Expanded = true;
    group.Selected = false;
    group.Indeterminate = false;
    group.Tasks =
      (group.Tasks &&
        group.Tasks.reduce((acc, task) => {
          const taskLookup = TasksById[task.TaskId];
          if (taskLookup) {
            task.AssignedStaffId = taskLookup.StaffId;
            task.WorkOrderTaskId = taskLookup.WorkOrderTaskId;
            task.AssignedStaffName =
              task.AssignedStaffId > 0
                ? cacheManagementService.MasterData_GetMutli(
                    task.PropertyId,
                    task.AssignedStaffId,
                    EntityCodes.Staff,
                    null
                  )
                : '';
            task.Selected = false;
            acc.push(task);
          }
          return acc;
        }, [])) ||
      [];
    group.TotalTime = group.Tasks.reduce(
      (acc, task) => acc + (task.EstimatedTime || 0),
      0
    );
    const allAssignedStaff = keys(groupBy('AssignedStaffId')(group.Tasks));
    group.AssignedStaffId =
      allAssignedStaff.length === 1 ? allAssignedStaff[0] : null;
    group.AssignedStaffName = group.AssignedStaffId
      ? cacheManagementService.MasterData_GetMutli(
          group.PropertyId,
          group.AssignedStaffId,
          EntityCodes.Staff,
          null
        )
      : null;
    return group;
  }).filter((group) => {
    return group.Tasks && group.Tasks.length;
  });
}

export class TaskGroupHelper<T extends TaskInfo> {
  GetTaskGroupTasks(TaskGroups: TaskGroupInfoViewModel<T>[]): T[] {
    const Tasks: T[] = [];
    return (TaskGroups || []).reduce(
      (acc, taskGroup) => acc.concat(taskGroup.Tasks || []),
      Tasks
    );
  }

  UpdateTasks(TaskGroup: TaskGroupInfoViewModel<T>, AllTasks: Array<T>) {
    TaskGroup.Tasks = AllTasks;
    this.UpdateTotalTime(TaskGroup);
    return TaskGroup;
  }

  GetAllGroupTime(TaskGroups: TaskGroupInfoViewModel<T>[]) {
    return (
      TaskGroups &&
      TaskGroups.reduce(
        (TotalTime, TaskGroup) => TotalTime + (TaskGroup.TotalTime || 0),
        0
      )
    );
  }

  UpdateTotalTime(TaskGroup: TaskGroupInfoViewModel<T>) {
    TaskGroup.TotalTime =
      TaskGroup.Tasks &&
      TaskGroup.Tasks.reduce(
        (total, task) => total + task.EstimatedTime || 0,
        0
      );
  }

  RemoveTaskFromGroups(
    Collection: TaskGroupInfoViewModel<T>[],
    TaskGroupId: number,
    TaskId: number
  ) {
    const [TaskGroup] = Collection.filter(
      (Group) => Group.TaskGroupId === TaskGroupId
    );
    if (!TaskGroup) {
      return;
    }
    this.RemoveTaskFromTasks(TaskGroup.Tasks, TaskId);
    return TaskGroup;
  }

  RemoveTaskFromTasks(Collection: Array<T>, TaskId: number) {
    const index = findIndex((Task: T) => Task.TaskId === TaskId)(Collection);
    if (index >= 0) {
      Collection.splice(index, 1);
    }
    return Collection;
  }

  RemoveId(Collection: number[], Id: number) {
    const index = findIndex((Item: number) => Item === Id)(Collection);
    if (index >= 0) {
      Collection.splice(index, 1);
    }
    return Collection;
  }

  RemoveGroup(Collection: TaskGroupInfoViewModel<T>[], TaskGroupId: number) {
    const index = findIndex(
      (Group: TaskGroupInfoViewModel<T>) => Group.TaskGroupId === TaskGroupId
    )(Collection);
    if (index >= 0) {
      Collection.splice(index, 1);
    }
    return Collection;
  }
}

export class TaskGroupTasksUpdate {
  public Tasks: Array<TaskUpdate>;
  public TaskGroup: TaskGroupUpdate;

  public setDefault() {
    this.Tasks = [];
    this.TaskGroup = new TaskGroupUpdate();
  }
}
